import { DetailField } from "./DetailField";
import { Select, TextInput } from "./Field";
import { Icon } from "./Icon";
import { ESTIMATE_SCALE, describeDays } from "../../../src/shared/capacity";
import type { RequestStatus } from "../data/types";

/**
 * The two capacity fields in a request's aside: how big it is, and when work
 * on it began.
 *
 * The estimate is a select, not a number box, so the only values that can be
 * sent are the five on the team's scale — there is nothing to validate. The
 * start date is filled by the server each time the request enters In
 * progress, so most people never touch it; it appears only once it has a
 * value or could get one, and stays visible afterwards because remaining
 * effort is derived from it.
 */
export function SizingFields({
  estimateDays,
  startedAt,
  status,
  disabled,
  onPatch,
}: {
  estimateDays: number | null;
  startedAt: string | null;
  status: RequestStatus;
  disabled: boolean;
  onPatch: (patch: { estimateDays?: number | null; startedAt?: string | null }) => void;
}) {
  return (
    <>
      <DetailField label="Estimate">
        <Select aria-label="Estimate" value={estimateDays ?? ""} disabled={disabled} onChange={(e) => onPatch({ estimateDays: e.target.value ? Number(e.target.value) : null })}>
          <option value="">Not estimated</option>
          {ESTIMATE_SCALE.map((d) => (
            <option key={d} value={d}>
              {describeDays(d)}
            </option>
          ))}
        </Select>
        <p className="mt-1 text-xs text-[var(--text-2)]">Days of work through Done, rework included. 1 day = 1 point in ClickUp. Over 5? Split it.</p>
      </DetailField>
      {(startedAt !== null || status === "progress") && (
        <DetailField label="Started">
          <div className="flex items-center gap-2">
            <TextInput type="date" aria-label="Started" value={startedAt ?? ""} disabled={disabled} onChange={(e) => onPatch({ startedAt: e.target.value || null })} />
            {startedAt && (
              <button
                type="button"
                onClick={() => onPatch({ startedAt: null })}
                disabled={disabled}
                aria-label="Clear start date"
                className="flex-none rounded p-1 text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring"
              >
                <Icon name="x" />
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-[var(--text-2)]">Set each time it enters In progress. Correct it if this stretch began earlier.</p>
        </DetailField>
      )}
    </>
  );
}
